import { Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, styled, tableCellClasses } from "@mui/material"
import { FormBuilder } from "components/FormBuilder/FormBuilder"
import React from 'react'
import { useTranslation } from "react-i18next"
import useColors from "utils/useColors/useColors"
import { getCardTypeIcon } from "utils/utils"

const MdrTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    color: "white",
  },
  width: 0, // Hack to force equal column grow
  padding: "16px 24px",
}))

/**
 * @typedef {"VISA"|"MASTER"|"WECHAT"|"ALIPAY"|"UNIONPAY"} processorCardType
 * @typedef {"mdr"|"foreignMdr"|"salesCommission"|"referrerCommission"|"remark"} processorCardTypeFields
 * 
 * @typedef {Object} processorGatewaySpecialHandling
 * @property {(value: any, fieldProps: import("components/FormBuilder/lib/FormBuilderFieldBuilder").formPropsType) => void} enabledOnCheckFn
 * 
 * @typedef {Object} processorCardTypeDefaultOptions
 * @property {string} mdr
 * @property {string} foreignMdr
 * @property {string} salesCommission
 * @property {string} referrerCommission
 * @property {string} remark
 * 
 * @typedef {Object} processorCardTypeConfig
 * @property {processorCardType} cardTypeKey
 * @property {processorCardTypeFields[]} [unavailableFields]
 * @property {processorCardTypeDefaultOptions} [defaultValues]
 * @property {string[]} [specialHandling]
 */

/**
 * Processor form value type
 * @typedef {object} processorFormCardTypeObject
 * @property {string} mdr
 * @property {string} foreignMdr
 * @property {string} salesCommission
 * @property {string} referrerCommission
 * @property {string} remark
 * 
 * @typedef {Record<processorCardType, processorFormCardTypeObject>} processorFormValue
 */

const UnavailablePlaceholder = () => <Box>-</Box>

const cardTypeCellWidth = "150px";
const cellWidth = "150px";
const wideCellWidth = "300px";

/**
 * @param {Object} props 
 * @param {import("components/FormBuilder/lib/FormBuilderFieldBuilder").formPropsType} props.formProps
 * @param {processorCardTypeConfig[]} props.config
 * @param {boolean} [props.isEditing=false]
 * @param {boolean} [props.isDisabled=false]
 */
const MdrTable = ({
  formProps,
  isEditing = false,
  isDisabled = false,
  config,
}) => {
  const { t: tLocal } = useTranslation("translation", { keyPrefix: "component.form.onlineMdrTable" });
  // const { t: tCommon } = useTranslation();
  const c = useColors();

  return (
    <TableContainer component={Paper} sx={{
      border: `1px solid e0e0e0`,
      boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
      overflowX: "auto"
    }}>
      <Table>
        <TableHead sx={{ backgroundColor: c.brandDark25, color: "white" }}>
          <TableRow>
            <MdrTableCell sx={{ minWidth: cardTypeCellWidth }}>
              {tLocal("columns.cardType")}
            </MdrTableCell>
            <MdrTableCell sx={{ minWidth: cellWidth }}>
              {tLocal("columns.mdr")}
            </MdrTableCell>
            <MdrTableCell sx={{ minWidth: cellWidth }}>
              {tLocal("columns.foreignMdr")}
            </MdrTableCell>
            <MdrTableCell sx={{ minWidth: cellWidth }}>
              {tLocal("columns.salesCommission")}
            </MdrTableCell>
            <MdrTableCell sx={{ minWidth: cellWidth }}>
              {tLocal("columns.referrerCommission")}
            </MdrTableCell>
            <MdrTableCell sx={{ minWidth: wideCellWidth }}>
              {tLocal("columns.remark")}
            </MdrTableCell>
          </TableRow>
        </TableHead>
        <TableBody sx={{ color: c.black }}>
          {config.map((c) => {

            const formKey = `mdrList.${c.cardTypeKey}`;

            /** @type {Record<string, import("components/FormBuilder/lib/FormBuilderFieldBuilder").formBuilderFieldConfig>} */
            const tableFields = {
              mdr: {
                fieldType: isEditing ? "textInput" : "textDisplay",
                fieldName: `${formKey}.mdr`,
                hideLabel: "removePadding",
                noErrorMessage: true,
                mandatory: true,
                disabled: isDisabled,
                config: {
                  inputType: "percentage",
                  asNumber: true,
                  numberRange: [0, 100],
                  defaultValue: c.defaultValues?.mdr,
                  rules: ["onlyPositiveNumber"],

                  suffix: " %"
                }
              },
              foreignMdr: {
                fieldType: isEditing ? "textInput" : "textDisplay",
                fieldName: `${formKey}.foreignMdr`,
                hideLabel: "removePadding",
                noErrorMessage: true,
                mandatory: true,
                disabled: isDisabled,
                config: {
                  inputType: "percentage",
                  asNumber: true,
                  numberRange: [0, 100],
                  defaultValue: c.defaultValues?.foreignMdr,
                  rules: ["onlyPositiveNumber"],

                  suffix: " %"
                }
              },
              salesCommission: {
                fieldType: isEditing ? "textInput" : "textDisplay",
                fieldName: `${formKey}.salesCommission`,
                hideLabel: "removePadding",
                noErrorMessage: true,
                mandatory: true,
                disabled: isDisabled,
                config: {
                  inputType: "percentage",
                  asNumber: true,
                  numberRange: [0, 100],
                  defaultValue: c.defaultValues?.salesCommission,
                  rules: ["onlyPositiveNumber"],

                  suffix: " %"
                }
              },
              referrerCommission: {
                fieldType: isEditing ? "textInput" : "textDisplay",
                fieldName: `${formKey}.referrerCommission`,
                hideLabel: "removePadding",
                noErrorMessage: true,
                mandatory: true,
                disabled: isDisabled,
                config: {
                  inputType: "percentage",
                  asNumber: true,
                  numberRange: [0, 100],
                  defaultValue: c.defaultValues?.referrerCommission,
                  rules: ["onlyPositiveNumber"],

                  suffix: " %"
                }
              },
              remark: {
                fieldType: isEditing ? "textInput" : "textDisplay",
                fieldName: `${formKey}.remark`,
                hideLabel: "removePadding",
                noErrorMessage: true,
                disabled: isDisabled,
                config: {
                  defaultValue: c.defaultValues?.remark,
                }
              }
            }

            const renderCell = (fieldName) => {
              return (
                <MdrTableCell>
                  {c.unavailableFields?.includes(fieldName) ? <UnavailablePlaceholder /> : FormBuilder.buildFields({ formProps, fields: [tableFields[fieldName]] })}
                </MdrTableCell>
              )
            }

            return <TableRow key={c.cardTypeKey}>
              <MdrTableCell sx={{ height: "60px" }}>
                <img
                  src={getCardTypeIcon(c.cardTypeKey)}
                  height={36}
                />
              </MdrTableCell>
              {renderCell("mdr")}
              {renderCell("foreignMdr")}
              {renderCell("salesCommission")}
              {renderCell("referrerCommission")}
              {renderCell("remark")}
            </TableRow>
          })}
        </TableBody>
      </Table>
    </TableContainer >
  )
}

export default React.memo(MdrTable);